import { routeBulletPresentation } from '../routeBulletPresentation.js';
import type { VitalDef } from './vitals.js';

/**
 * Collapse duplicate normalized route IDs while preserving visually similar
 * but semantically distinct routes such as `7` and `7X`.
 */
function dedupeByBullet(routes: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const route of routes) {
    const presentation = routeBulletPresentation(route);
    const key = presentation.shape === 'diamond' ? presentation.id : presentation.label;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(presentation.id);
  }
  return out;
}

/** MTA text uses bracketed route IDs where its own UI inserts route bullets. */
function withoutLeadingRouteTokens(header: string): string {
  return header.replace(/^\s*(?:\[[^\]]+\]\s*)+/, '').trim();
}

/** Vital for "lines reporting delays" from the MTA service alerts feed. */
export const delaysVital: VitalDef = {
  id: 'delays',
  layerId: 'service_alerts',
  select: (s) => {
    const down = !s || s.status === 'down';
    const stale = s?.status === 'stale';

    if (down || !s?.summary) {
      return {
        value: '—',
        label: 'delayed',
        down: true,
        stale: false,
      };
    }

    const summary = s.summary as {
      delayedLines: string[];
      count: number;
      alerts?: { routes: string[]; header: string }[];
    };
    const bullets = dedupeByBullet(summary.delayedLines ?? []);
    const count = bullets.length;

    if (count === 0) {
      return {
        value: '0',
        label: 'delayed',
        down: false,
        stale,
        tone: 'good',
      };
    }

    return {
      value: `${count}`,
      label: 'delayed',
      down: false,
      stale,
      tone: 'warn',
      bullets,
      detailTitle: 'Service alerts',
      details: summary.alerts?.map(({ routes, header }) => ({
        kind: 'routes',
        routes,
        text: withoutLeadingRouteTokens(header),
      })),
    };
  },
};
